import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../lib/firebase';
import type { BackBookRadarResponse } from './types';

/**
 * RBAC-scoped back book radar via the getBackBookRadar callable (broker -> own
 * clients, admin -> office, super_admin -> company). The broker filter is
 * applied server side, before the top-200 cap, so an admin filtering to one
 * broker sees that broker's full top list rather than a slice of the company's.
 */
export function useBackBookRadar(companyId: string | undefined, brokerDisplay: string | null) {
  const [data, setData] = useState<BackBookRadarResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<{ companyId: string; brokerDisplay?: string }, BackBookRadarResponse>(
        functions,
        'getBackBookRadar',
      );
      const res = await fn({ companyId, ...(brokerDisplay ? { brokerDisplay } : {}) });
      setData(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the back book radar');
      console.error('[useBackBookRadar] failed:', e);
    } finally {
      setLoading(false);
    }
  }, [companyId, brokerDisplay]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, loading, error, reload };
}
